import { useEffect, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBug,
  faLightbulb,
  faLifeRing,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type {
  ActionItemType,
  GuardianTicketV2,
  RatingLevel,
} from "@/services/api/guardiansv2/types";

const SEVERITY_STRIP: Record<RatingLevel, string> = {
  red: "bg-rose-400/80",
  amber: "bg-amber-400/80",
  green: "bg-emerald-500/40",
};

const TYPE_ICONS: Record<ActionItemType, typeof faBug> = {
  bug: faBug,
  feature_request: faLightbulb,
  client_support: faLifeRing,
};

export interface LinkedTicketCardProps {
  ticketRef: string;
  onOpen: () => void;
}

/** Compact ticket summary for audit inspector links. */
export function LinkedTicketCard({ ticketRef, onOpen }: LinkedTicketCardProps) {
  const [ticket, setTicket] = useState<GuardianTicketV2 | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    GuardiansV2API.getTicket(ticketRef)
      .then((doc) => {
        if (mounted) setTicket(doc);
      })
      .catch(() => {
        if (mounted) setTicket(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [ticketRef]);

  if (loading) {
    return (
      <div
        className="h-12 animate-pulse rounded-lg border border-white/10 bg-slate-800/60"
        data-testid={`linked-ticket-loading-${ticketRef}`}
      />
    );
  }

  if (!ticket) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="w-full rounded-lg border border-white/10 bg-slate-800/60 px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-800"
        data-testid={`linked-ticket-${ticketRef}`}
      >
        {ticketRef}
      </button>
    );
  }

  const severityStrip = SEVERITY_STRIP[ticket.severity] ?? SEVERITY_STRIP.green;
  const typeIcon = TYPE_ICONS[ticket.type] ?? faBug;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group w-full overflow-hidden rounded-lg border border-white/10 bg-slate-800/60 text-left transition-colors hover:border-white/20 hover:bg-slate-800"
      data-testid={`linked-ticket-${ticketRef}`}
    >
      <div className={clsx("h-0.5 w-full", severityStrip)} />
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-900/70 text-slate-400">
          <FontAwesomeIcon icon={typeIcon} className="text-xs" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-100 group-hover:text-white">
            {ticket.title}
          </span>
        </span>
      </div>
    </button>
  );
}
